import { LDateTime } from '@app/components/date/LDateTime';
import { PlayAdOverlayButton } from '@app/modules/audit/modules/station-audit/components/audit-result/PlayAdOverlayButton';
import { Seconds, toTimestamp } from '@mi-front/core';
import { Duration, MrtTable, NullState } from '@ui/components';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { filterAdoverlay, FormattedAdoverlayType } from './utils';
import { Station } from '@app/core/types/stations';

interface IAdoverlayView {
  data: FormattedAdoverlayType[] | undefined;
  loading: boolean;
  station: Station;
}

const AdoverlayView: React.FunctionComponent<IAdoverlayView> = memo((props) => {
  const { data, loading, station } = props;
  const { formatMessage } = useIntl();

  if (data && data.length > 0) {
    return (
      <MrtTable<FormattedAdoverlayType>
        filterFns={{
          filterAdoverlay: filterAdoverlay,
        }}
        globalFilterFn="filterAdoverlay"
        columns={[
          {
            id: 'time',
            header: formatMessage({
              id: 'search.dateTime',
              defaultMessage: 'Fecha y hora',
            }),
            enableColumnFilter: false,
            accessorFn: (row) => row.date,
            Cell: ({ row: { original } }) => <LDateTime value={original.date} showSeconds />,
            enableSorting: true,
            size: 60,
          },
          {
            id: 'brand',
            enableGlobalFilter: true,
            enableSorting: false,
            enableColumnFilter: false,
            accessorFn: (row) => row.brand,
            header: formatMessage({
              id: 'audit.adoverlayScreen.brand',
              defaultMessage: 'Marca',
            }),
            Cell: ({ renderedCellValue }) => <div>{renderedCellValue}</div>,
            size: 50,
          },
          {
            id: 'image',
            header: formatMessage({
              id: 'audit.adoverlayScreen.image',
              defaultMessage: 'Imagen',
            }),
            Cell: ({ row: { original } }) => (
              <div>
                <img
                  src={original.image_b64}
                  alt={`${original.brand} ad`}
                  style={{ maxWidth: '42rem', height: '6rem' }}
                />
              </div>
            ),
          },
          {
            id: 'duration',
            header: formatMessage({
              id: 'audit.adoverlayScreen.duration',
              defaultMessage: 'Duración',
            }),
            Cell: ({ row: { original } }) => (
              <div>
                <Duration
                  seconds={
                    (toTimestamp(original.end_time) - toTimestamp(original.start_time)) as Seconds
                  }
                />
              </div>
            ),
            enableSorting: true,
            size: 30,
          },
          {
            id: 'actions',
            header: formatMessage({
              id: 'common.text.actions',
              defaultMessage: 'Acciones',
            }),
            size: 20,
            Cell: ({ row: { original } }) => (
              <PlayAdOverlayButton adOverlay={original} station={station} />
            ),
          },
        ]}
        data={data}
        enableExpandAll={false}
        enableGlobalFilter={true}
        enableFilters={true}
        enableHiding={false}
        enableColumnActions={false}
        state={{
          isLoading: loading,
        }}
      />
    );
  }

  return (
    <NullState className="p-7">
      {loading ? (
        <FormattedMessage
          id="audit.adoverlayScreen.loading"
          defaultMessage="Cargando ad overlays"
          values={{ count: 0 }}
        />
      ) : (
        <FormattedMessage
          id="audit.adoverlayScreen.noResults"
          defaultMessage="No se encontraron ad overlays"
          values={{ count: 0 }}
        />
      )}
    </NullState>
  );
});

export default AdoverlayView;
